import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Menu, X, User, LogOut, LayoutDashboard, ChevronDown } from 'lucide-react';
import AuthModal from './AuthModal';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const PETSBOX_LOGO = "https://customer-assets.emergentagent.com/job_paws-box/artifacts/gyymiifm_f89199bc-32c1-4646-a02f-5de9041d5b0a.png";

export default function Navbar({ onSubscribeClick }) {
  const [isOpen, setIsOpen] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const { user, logout, isAdmin } = useAuth();
  const { t, language, toggleLanguage } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  const navLinks = [
    { href: '/#how-it-works', label: t('nav_how_it_works') },
    { href: '/#benefits', label: t('nav_benefits') },
    { href: '/#whats-inside', label: t('nav_whats_inside') },
    { href: '/#faq', label: t('nav_faq') },
  ];

  const handleNavClick = (href) => {
    setIsOpen(false);
    if (href.startsWith('/#')) {
      if (location.pathname !== '/') {
        navigate('/');
        setTimeout(() => {
          const element = document.getElementById(href.substring(2));
          element?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      } else {
        const element = document.getElementById(href.substring(2));
        element?.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleSubscribe = () => {
    setIsOpen(false);
    if (onSubscribeClick) {
      onSubscribeClick();
    } else {
      navigate('/subscribe');
    }
  };

  return (
    <>
    <nav className="fixed top-0 left-0 right-0 bg-brand-cream/95 backdrop-blur-md z-50 border-b border-brand-border/50">
      <div className="section-container">
        <div className="flex items-center justify-between h-20">
          {/* Left: Desktop Navigation Links */}
          <div className="hidden lg:flex items-center gap-5 flex-1">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => handleNavClick(link.href)}
                className="text-brand-muted hover:text-brand-brown font-medium transition-colors text-sm"
              >
                {link.label}
              </button>
            ))}
          </div>

          {/* Mobile: hamburger on left */}
          <div className="lg:hidden flex items-center">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 text-brand-brown"
              data-testid="mobile-menu-toggle"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Center: Logo only (overlapping below) */}
          <Link to="/" className="relative z-10 flex-shrink-0" data-testid="navbar-logo">
            <div className="relative">
              <img 
                src={PETSBOX_LOGO} 
                alt="PetsBox" 
                className="w-20 h-20 md:w-24 md:h-24 rounded-full shadow-lg border-4 border-white bg-white translate-y-4 md:translate-y-6" 
              />
            </div>
          </Link>

          {/* Right: Language, Auth, Subscribe */}
          <div className="hidden lg:flex items-center gap-3 flex-1 justify-end">
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-2 px-3 py-2 rounded-full bg-white border border-brand-border hover:border-brand-orange transition-colors"
              data-testid="language-toggle"
            >
              {language === 'ro' ? (
                <span className="text-lg">🇷🇴</span>
              ) : (
                <span className="text-lg">🇬🇧</span>
              )}
              <span className="text-sm font-semibold text-brand-brown">
                {language === 'ro' ? 'RO' : 'EN'}
              </span>
            </button>

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button 
                    className="flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-brand-border hover:border-brand-orange transition-colors"
                    data-testid="user-menu-trigger"
                  >
                    {user.picture ? (
                      <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                    ) : (
                      <User className="w-5 h-5 text-brand-muted" />
                    )}
                    <span className="font-medium text-brand-brown">{user.name?.split(' ')[0]}</span>
                    <ChevronDown className="w-4 h-4 text-brand-muted" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-white">
                  <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="menu-dashboard">
                    <LayoutDashboard className="w-4 h-4 mr-2" />
                    {t('nav_my_profile')}
                  </DropdownMenuItem>
                  {isAdmin && (
                    <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="menu-admin">
                      <User className="w-4 h-4 mr-2" />
                      {t('nav_admin')}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} data-testid="menu-logout">
                    <LogOut className="w-4 h-4 mr-2" />
                    {t('nav_logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <button
                onClick={() => setShowAuth(true)}
                className="text-brand-brown font-medium hover:text-brand-orange transition-colors"
                data-testid="login-btn"
              >
                {t('nav_login')}
              </button>
            )}
            
            <button
              onClick={handleSubscribe}
              className="btn-primary text-base px-6 py-3"
              data-testid="subscribe-btn"
            >
              {t('nav_subscribe')}
            </button>
          </div>

          {/* Mobile: Language toggle on right */}
          <div className="lg:hidden flex items-center gap-2">
            <button
              onClick={toggleLanguage}
              className="p-2 rounded-full bg-white border border-brand-border"
              data-testid="mobile-language-toggle"
            >
              {language === 'ro' ? '🇷🇴' : '🇬🇧'}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="lg:hidden py-4 border-t border-brand-border/50">
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <button
                  key={link.href}
                  onClick={() => handleNavClick(link.href)}
                  className="text-left text-brand-muted hover:text-brand-brown font-medium py-2"
                >
                  {link.label}
                </button>
              ))}
              <div className="border-t border-brand-border/50 pt-4 flex flex-col gap-3">
                {user ? (
                  <>
                    <Link
                      to="/dashboard"
                      onClick={() => setIsOpen(false)}
                      className="flex items-center gap-2 text-brand-brown font-medium"
                    >
                      <LayoutDashboard className="w-5 h-5" />
                      {t('nav_my_profile')}
                    </Link>
                    {isAdmin && (
                      <Link
                        to="/admin"
                        onClick={() => setIsOpen(false)}
                        className="flex items-center gap-2 text-brand-brown font-medium"
                      >
                        <User className="w-5 h-5" />
                        {t('nav_admin')}
                      </Link>
                    )}
                    <button
                      onClick={logout}
                      className="flex items-center gap-2 text-brand-muted font-medium"
                    >
                      <LogOut className="w-5 h-5" />
                      {t('nav_logout')}
                    </button>
                  </>
                ) : (
                  <button onClick={() => { setShowAuth(true); setIsOpen(false); }} className="text-left text-brand-brown font-medium py-2">
                    {t('nav_login')}
                  </button>
                )}
                <button
                  onClick={handleSubscribe}
                  className="btn-primary text-center"
                >
                  {t('nav_subscribe')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
    <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} />
    </>
  );
}

export { PETSBOX_LOGO };
